import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import StepNavigation from "../components/StepNavigation";
import TopicStep from "../components/steps/TopicStep";
import ScriptStep from "../components/steps/ScriptStep";
import SceneReviewStep from "../components/steps/SceneReviewStep";
import AssetsStep from "../components/steps/AssetsStep";
import PreviewStep from "../components/steps/PreviewStep";
import EditorStep from "../components/steps/EditorStep";
import ScriptActions from "Redux/ScriptRedux";
import AudioActions from "Redux/AudioRedux";
import VideoActions from "Redux/VideoRedux";
import DraftActions from "Redux/DraftRedux";

const STEPS = {
  TOPIC: 1,
  SCRIPT: 2,
  SCENES: 3,
  PREVIEW: 4,
  ASSETS: 5,
  EDITOR: 6,
};

export default function HomePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [currentStep, setCurrentStep] = useState(STEPS.TOPIC);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  const [activeTab, setActiveTab] = useState("idea"); // "idea" or "script"
  const [topic, setTopic] = useState("");
  const [videoSettings, setVideoSettings] = useState({
    videoType: "short",
    aspectRatio: "9:16",
    duration: 30,
    contentStyle: "informative",
    imageFrequency: 10,
    subtitles: true,
    voice: "neutral",
    resolution: "1080p",
    imageStyle: "",
    characterImage: null,
    characterLabel: "",
    useCharacterReference: false,
    // Animation settings
    animateVisuals: false,
    animationScope: "all",
    // Voice settings
    voiceId: "af_bella",
    voiceName: "Bella",
  });
  const [script, setScript] = useState("");
  const [scriptId, setScriptId] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  const [audioStatus, setAudioStatus] = useState("pending");
  const [thumbnailStatus, setThumbnailStatus] = useState("pending");
  const [assetsProgress, setAssetsProgress] = useState(0);
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState("2-3 mins");
  
  const [error, setError] = useState("");
  
  const [audioUrl, setAudioUrl] = useState("");
  const [audioId, setAudioId] = useState(null);
  const [images, setImages] = useState([]);
  const [segments, setSegments] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoId, setVideoId] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [videoHistory, setVideoHistory] = useState([]);
  const [subtitleData, setSubtitleData] = useState("");
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const [subtitleStyle, setSubtitleStyle] = useState("default");
  const [subtitleSettings, setSubtitleSettings] = useState(null);
  
  // Sound effects state
  const [soundEffectClips, setSoundEffectClips] = useState([]);
  
  // Watermark state (based on team plan - defaults to true for new scripts)
  const [showWatermark, setShowWatermark] = useState(true);
  
  const saveTimeoutRef = useRef(null);

  const loadVideoHistory = useCallback((scriptIdToLoad) => {
    if (!scriptIdToLoad) return;
    dispatch(VideoActions.videoListRequest(scriptIdToLoad, (data, error) => {
      if (error) {
        console.error("Failed to load video history:", error);
        return;
      }
      setVideoHistory(data || []);
    }));
  }, [dispatch]);

  const updateWordCount = (text) => {
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    setWordCount(words);
  };

  const handleScriptChange = (newScript) => {
    setScript(newScript);
    updateWordCount(newScript);
  };

  const startAudioGenerationForScript = (targetScriptId, retryCount = 0) => {
    const options = {
      voiceId: videoSettings.voiceId,
      voiceName: videoSettings.voiceName,
    };

    dispatch(AudioActions.audioGenerateRequest(targetScriptId, options, (audio, audioError) => {
      if (audioError) {
        const message = String(audioError || "");
        const isNotReadyError = /script must be completed before generating audio/i.test(message);

        if (isNotReadyError && retryCount < 3) {
          setEstimatedTimeLeft("Finalizing script...");
          dispatch(ScriptActions.scriptPollRequest(targetScriptId, (completed, pollError) => {
            if (pollError) {
              setError(pollError || "Failed to prepare script for asset generation");
              console.error("Script readiness poll error:", pollError);
              setAudioStatus("failed");
              setThumbnailStatus("failed");
              return;
            }
            startAudioGenerationForScript(targetScriptId, retryCount + 1);
          }));
          return;
        }

        setError(audioError || "Failed to generate assets");
        console.error("Asset generation error:", audioError);
        setAudioStatus("failed");
        setThumbnailStatus("failed");
        return;
      }

      // Audio completed - same flow as ScriptDetailPage
      setAudioUrl(audio.audio_url);
      setAudioId(audio.id);
      setSubtitleData(audio.subtitle_data || "");
      setAudioStatus("completed");
      setAssetsProgress(75);
      setEstimatedTimeLeft("30-60 secs");

      // Poll until thumbnail_images are ready (same as ScriptDetailPage)
      let thumbnailAttempts = 0;
      const maxThumbnailAttempts = 60;

      const pollThumbnails = () => {
        dispatch(ScriptActions.scriptDetailRequest(targetScriptId, (scriptData, scriptError) => {
          if (scriptError) {
            setError(scriptError || "Failed to load thumbnails");
            setThumbnailStatus("failed");
            return;
          }

          const allThumbnails = scriptData.thumbnail_images || [];
          const completedThumbnails = allThumbnails.filter(t => t.status === "completed" && t.image_url);
          const failedThumbnails = allThumbnails.filter(t => t.status === "failed");
          const pendingThumbnails = allThumbnails.filter(t =>
            t.status === "pending" || t.status === "processing" || t.status === "generating_prompt"
          );

          if (allThumbnails.length > 0) {
            const thumbnailProgress = (completedThumbnails.length / allThumbnails.length) * 20;
            setAssetsProgress(Math.min(95, 75 + thumbnailProgress));
          }

          // Done when no pending remain (include both completed + failed in scenes)
          if (pendingThumbnails.length === 0 && (completedThumbnails.length > 0 || failedThumbnails.length > 0)) {
            // Include all thumbnails (completed + failed) so failed ones show in Scenes page
            const allFinishedThumbnails = allThumbnails.filter(t => 
              (t.status === "completed" && t.image_url) || t.status === "failed"
            );
            finishWithThumbnails(allFinishedThumbnails, targetScriptId, scriptData);
            return;
          }

          thumbnailAttempts++;
          if (thumbnailAttempts >= maxThumbnailAttempts) {
            const allFinishedThumbnails = allThumbnails.filter(t => 
              (t.status === "completed" && t.image_url) || t.status === "failed"
            );
            if (allFinishedThumbnails.length > 0) {
              finishWithThumbnails(allFinishedThumbnails, targetScriptId, scriptData);
            } else {
              setError("No thumbnail images were generated. Please try again.");
              setThumbnailStatus("failed");
            }
            return;
          }

          setTimeout(pollThumbnails, 3000);
        }));
      };

      const finishWithThumbnails = (thumbnails, finishedScriptId, scriptData) => {
        // Set watermark based on team plan from script data
        if (scriptData && scriptData.show_watermark !== undefined) {
          setShowWatermark(scriptData.show_watermark !== false);
        }
        
        setImages(thumbnails.map((t, i) => ({
          id: t.id || i + 1,
          image_url: t.image_url,
          prompt: t.prompt,
          start_time: t.start_time,
          end_time: t.end_time,
          status: t.status,
          error_message: t.error_message,
          animation_status: t.animation_status,
          animation_prompt: t.animation_prompt,
          animated_video_url: t.animated_video_url,
          animation_error_message: t.animation_error_message,
        })));

        setThumbnailStatus("completed");
        setAssetsProgress(95);

        // Try to load draft segments
        dispatch(DraftActions.draftGetRequest(finishedScriptId, (draft, draftError) => {
          let hasRestoredSegments = false;

          if (!draftError && draft && draft.segments && draft.segments.length > 0) {
            const restoredSegments = draft.segments.map((seg, i) => ({
              id: seg.id || `segment-${i}`,
              thumbnail_image_id: seg.thumbnail_image_id || seg.id,
              image_url: seg.image_url,
              prompt: seg.prompt,
              startTime: seg.start_time,
              endTime: seg.end_time,
              animated_video_url: seg.animated_video_url,
              animation_status: seg.animation_status,
              animation_prompt: seg.animation_prompt || "",
            }));
            setSegments(restoredSegments);
            hasRestoredSegments = true;

            if (draft.subtitles_enabled !== undefined) {
              setSubtitlesEnabled(draft.subtitles_enabled);
            }
            if (draft.subtitle_settings) {
              setSubtitleSettings(draft.subtitle_settings);
            }
            if (draft.sound_effect_clips && draft.sound_effect_clips.length > 0) {
              setSoundEffectClips(draft.sound_effect_clips);
            }
          }

          if (!hasRestoredSegments) {
            setSegments(thumbnails.map((t, i) => ({
              id: t.id || `segment-${i}`,
              thumbnail_image_id: t.id,
              image_url: t.image_url,
              prompt: t.prompt,
              startTime: t.start_time || 0,
              endTime: t.end_time || 0,
              status: t.status,
              error_message: t.error_message,
              animation_status: t.animation_status,
              animation_prompt: t.animation_prompt || "",
              animated_video_url: t.animated_video_url,
              animate_enabled: t.animation_status === "completed",
            })));
          }

          setAssetsProgress(100);
          setCompletedSteps([STEPS.TOPIC, STEPS.SCRIPT, STEPS.ASSETS]);
          setCurrentStep(STEPS.SCENES);

          // Update backend with current step
          dispatch(ScriptActions.scriptUpdateStepRequest(finishedScriptId, 'scenes', (data, stepErr) => {
            if (stepErr) {
              console.error("Failed to update script step:", stepErr);
            }
          }));
        }));
      };

      // Start polling for thumbnails
      pollThumbnails();
    }));
  };

  const handleGenerateScript = () => {
    if (!topic.trim()) return;

    setIsGeneratingScript(true);
    setError("");

    dispatch(ScriptActions.scriptCreateRequest(topic, videoSettings, (created, createError) => {
      if (createError) {
        setError(createError || "Failed to generate script");
        console.error("Script generation error:", createError);
        setIsGeneratingScript(false);
        return;
      }

      setScriptId(created.id);
      
      // Poll until script is complete
      dispatch(ScriptActions.scriptPollRequest(created.id, (completed, pollError) => {
        setIsGeneratingScript(false);
        
        if (pollError) {
          setError(pollError || "Failed to generate script");
          console.error("Script generation error:", pollError);
          return;
        }
        
        setScript(completed.content);
        updateWordCount(completed.content);
        setScriptId(completed.id);
        setCompletedSteps([STEPS.TOPIC]);
        setCurrentStep(STEPS.SCRIPT);
        
        // Navigate to script page after creation
        navigate(`/scripts/${completed.id}`);
      }));
    }));
  };

  const handleGenerateFromScript = () => {
    if (!script.trim()) return;

    setIsGeneratingScript(true);
    setError("");

    // Create script with user's content
    const settings = {
      ...videoSettings,
      manualEntry: true,
    };
    
    dispatch(ScriptActions.scriptCreateRequest(topic || "Untitled Script", settings, (created, createError) => {
      if (createError) {
        setError(createError || "Failed to create script");
        console.error("Script creation error:", createError);
        setIsGeneratingScript(false);
        return;
      }
      
      const newScriptId = created.id;
      setScriptId(newScriptId);
      
      // Save the script content
      dispatch(ScriptActions.scriptUpdateContentRequest(newScriptId, script, (updateData, updateError) => {
        if (updateError) {
          setError(updateError || "Failed to save script");
          console.error("Script save error:", updateError);
          setIsGeneratingScript(false);
          return;
        }

        // Switch to assets UI first, then wait for script readiness and generate.
        setIsGeneratingScript(false);
        setCompletedSteps([STEPS.TOPIC, STEPS.SCRIPT]);
        setCurrentStep(STEPS.ASSETS);
        setAssetsProgress(0);
        setAudioStatus("pending");
        setThumbnailStatus("pending");
        setEstimatedTimeLeft("Preparing script...");

        setAudioStatus("generating");
        setThumbnailStatus("generating");
        setAssetsProgress(10);

        // Attempt audio immediately; if backend says "script not completed",
        // guarded retry logic will poll readiness and retry.
        startAudioGenerationForScript(newScriptId, 0);
      }));
    }));
  };

  const handleRegenerateScript = () => {
    if (!scriptId) return;

    setIsRegenerating(true);
    setError("");

    dispatch(ScriptActions.scriptRegenerateRequest(scriptId, (data, regenError) => {
      if (regenError) {
        setError(regenError || "Failed to regenerate script");
        console.error("Script regeneration error:", regenError);
        setIsRegenerating(false);
        return;
      }

      setScript("");
      setWordCount(0);

      dispatch(ScriptActions.scriptPollRequest(scriptId, (completed, pollError) => {
        setIsRegenerating(false);
        
        if (pollError) {
          setError(pollError || "Failed to regenerate script");
          console.error("Script regeneration error:", pollError);
          return;
        }
        
        setScript(completed.content);
        updateWordCount(completed.content);
      }));
    }));
  };

  const handleBackToTopic = () => {
    setCurrentStep(STEPS.TOPIC);
    setCompletedSteps([]);
  };

  const handleGenerateAssets = () => {
    if (!scriptId) return;

    setCurrentStep(STEPS.ASSETS);
    setAssetsProgress(0);
    setAudioStatus("pending");
    setThumbnailStatus("pending");
    setEstimatedTimeLeft("2-3 mins");
    setError("");

    setAudioStatus("generating");
    setThumbnailStatus("generating");
    setAssetsProgress(10);

    startAudioGenerationForScript(scriptId, 0);
  };

  const handleCancelGeneration = () => {
    setCurrentStep(STEPS.SCRIPT);
    setAudioStatus("pending");
    setThumbnailStatus("pending");
    setAssetsProgress(0);
  };

  const debouncedSaveDraft = useCallback((scriptIdToSave, segmentsToSave, subtitlesEnabledValue, sfxClips = []) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      if (!scriptIdToSave || !segmentsToSave || segmentsToSave.length === 0) return;
      
      setIsSavingDraft(true);
      const options = { 
        subtitlesEnabled: subtitlesEnabledValue,
        subtitleSettings: subtitleSettings,
        soundEffectClips: sfxClips,
      };
      
      dispatch(DraftActions.draftSaveRequest(scriptIdToSave, audioUrl, segmentsToSave, options, (data, error) => {
        setIsSavingDraft(false);
        if (error) {
          console.error("Failed to save draft:", error);
        }
      }));
    }, 2000);
  }, [audioUrl, subtitleSettings, dispatch]);

  const handleSegmentsChange = (newSegments) => {
    setSegments(newSegments);
    if (scriptId && newSegments.length > 0) {
      debouncedSaveDraft(scriptId, newSegments, subtitlesEnabled, soundEffectClips);
    }
  };

  const handleSubtitlesChange = (enabled) => {
    setSubtitlesEnabled(enabled);
    if (scriptId && segments.length > 0) {
      debouncedSaveDraft(scriptId, segments, enabled, soundEffectClips);
    }
  };

  const handleSoundEffectClipsChange = (newClips) => {
    setSoundEffectClips(newClips);
    if (scriptId && segments.length > 0) {
      debouncedSaveDraft(scriptId, segments, subtitlesEnabled, newClips);
    }
  };

  const handleSubtitleSettingsChange = (newSettings) => {
    setSubtitleSettings(newSettings);
    if (scriptId && segments.length > 0) {
      debouncedSaveDraft(scriptId, segments, subtitlesEnabled, soundEffectClips);
    }
  };

  const handleExportVideo = (options = {}) => {
    if (!audioUrl || segments.length === 0 || !scriptId) return;

    setIsExporting(true);
    setCurrentStep(STEPS.ASSETS);
    setAssetsProgress(10);
    setEstimatedTimeLeft("2-3 mins");
    setError("");

    const videoOptions = { 
      subtitlesEnabled: options.subtitlesEnabled || false,
      subtitleStyle: options.subtitleStyle || subtitleStyle || 'default',
      subtitleSettings: options.subtitleSettings || null,
      soundEffectClips: soundEffectClips,
    };

    dispatch(VideoActions.videoGenerateRequest(scriptId, audioUrl, segments, videoOptions, (video, videoError) => {
      setIsExporting(false);
      
      if (videoError) {
        setError(videoError || "Failed to generate video");
        console.error("Video generation error:", videoError);
        setCurrentStep(STEPS.PREVIEW);
        return;
      }

      setVideoUrl(video.video_url);
      setVideoId(video.id);
      setAssetsProgress(100);
      setCompletedSteps([STEPS.TOPIC, STEPS.SCRIPT, STEPS.ASSETS, STEPS.PREVIEW]);
      
      // Refresh video history after successful export
      loadVideoHistory(scriptId);
      
      setCurrentStep(STEPS.PREVIEW);
      
      // Delete draft after successful export
      dispatch(DraftActions.draftDeleteRequest(scriptId, (data, deleteError) => {
        if (deleteError) {
          console.error("Failed to delete draft segments:", deleteError);
        }
      }));
    }));
  };

  const handleBackToScript = () => {
    setCurrentStep(STEPS.SCRIPT);
  };

  const handleBackToScenes = () => {
    setCurrentStep(STEPS.SCENES);
  };

  const handleScenesChange = (updatedScenes) => {
    setImages(updatedScenes.map((scene, i) => ({
      id: scene.id || i + 1,
      image_url: scene.image_url,
      prompt: scene.prompt,
      start_time: scene.startTime,
      end_time: scene.endTime,
      animation_status: scene.animation_status,
      animated_video_url: scene.animated_video_url,
      animate_enabled: scene.animate_enabled,
      animation_prompt: scene.animation_prompt || "",
    })));
    setSegments(updatedScenes);
  };

  const handleConfirmScenes = () => {
    setCompletedSteps([STEPS.TOPIC, STEPS.SCRIPT, STEPS.SCENES, STEPS.ASSETS]);
    setCurrentStep(STEPS.PREVIEW);
  };

  const handleCreateAnother = () => {
    setCurrentStep(STEPS.TOPIC);
    setCompletedSteps([]);
    setActiveTab("idea");
    setTopic("");
    setScript("");
    setScriptId(null);
    setWordCount(0);
    setAudioUrl("");
    setAudioId(null);
    setImages([]);
    setSegments([]);
    setVideoUrl("");
    setVideoId(null);
    setError("");
    setSubtitleStyle("default");
    setSoundEffectClips([]);
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.TOPIC:
        return (
          <TopicStep
            activeTab={activeTab}
            onTabChange={setActiveTab}
            topic={topic}
            onTopicChange={setTopic}
            script={script}
            onScriptChange={handleScriptChange}
            wordCount={wordCount}
            settings={videoSettings}
            onSettingsChange={setVideoSettings}
            onGenerateScript={handleGenerateScript}
            onGenerateFromScript={handleGenerateFromScript}
            isLoading={isGeneratingScript}
          />
        );
      case STEPS.SCRIPT:
        return (
          <ScriptStep
            script={script}
            onScriptChange={handleScriptChange}
            onRegenerate={handleRegenerateScript}
            onBack={handleBackToTopic}
            onNext={handleGenerateAssets}
            isRegenerating={isRegenerating}
            wordCount={wordCount}
          />
        );
      case STEPS.ASSETS:
        return (
          <AssetsStep
            progress={assetsProgress}
            estimatedTimeLeft={estimatedTimeLeft}
            audioStatus={audioStatus}
            thumbnailStatus={thumbnailStatus}
            onCancel={handleCancelGeneration}
          />
        );
      case STEPS.SCENES:
        const scenesData = segments.length > 0
          ? segments.map((seg, i) => {
              const matchingImage = images.find((img) =>
                img.id === seg.thumbnail_image_id ||
                img.id === seg.id ||
                (seg.image_url && img.image_url === seg.image_url)
              ) || images[i];
              const thumbnailId = seg.thumbnail_image_id || matchingImage?.id;
              return {
                id: seg.id,
                thumbnail_image_id: thumbnailId,
                image_url: seg.image_url,
                prompt: seg.prompt || matchingImage?.prompt || "",
                startTime: seg.startTime,
                endTime: seg.endTime,
                animation_status: seg.animation_status || matchingImage?.animation_status,
                animated_video_url: seg.animated_video_url || matchingImage?.animated_video_url,
                animate_enabled: seg.animate_enabled || false,
                animation_prompt: seg.animation_prompt || matchingImage?.animation_prompt || "",
              };
            })
          : images.map((img, i) => ({
              id: img.id || i + 1,
              thumbnail_image_id: img.id || i + 1,
              image_url: img.image_url,
              prompt: img.prompt || "",
              startTime: img.start_time || 0,
              endTime: img.end_time || 0,
              animation_status: img.animation_status,
              animated_video_url: img.animated_video_url,
              animate_enabled: img.animate_enabled || false,
              animation_prompt: img.animation_prompt || "",
            }));
        return (
          <SceneReviewStep
            scenes={scenesData}
            audioUrl={audioUrl}
            aspectRatio={videoSettings.aspectRatio}
            onScenesChange={handleScenesChange}
            onConfirm={handleConfirmScenes}
            onBack={handleBackToScript}
          />
        );
      case STEPS.PREVIEW:
        return (
          <PreviewStep
            audioUrl={audioUrl}
            audioId={audioId}
            images={images}
            savedSegments={segments}
            topic={topic}
            aspectRatio={videoSettings.aspectRatio}
            onSegmentsChange={handleSegmentsChange}
            onExportVideo={handleExportVideo}
            onBack={handleBackToScenes}
            isExporting={isExporting}
            isSavingDraft={isSavingDraft}
            videoHistory={videoHistory}
            videoId={videoId}
            videoUrl={videoUrl}
            scriptId={scriptId}
            onRefreshHistory={() => loadVideoHistory(scriptId)}
            subtitleData={subtitleData}
            subtitlesEnabled={subtitlesEnabled}
            onSubtitlesChange={handleSubtitlesChange}
            subtitleStyle={subtitleStyle}
            onSubtitleStyleChange={setSubtitleStyle}
            soundEffectClips={soundEffectClips}
            onSoundEffectClipsChange={handleSoundEffectClipsChange}
            showWatermark={showWatermark}
            initialSubtitleSettings={subtitleSettings}
            onSubtitleSettingsChange={handleSubtitleSettingsChange}
          />
        );
      case STEPS.EDITOR:
        return (
          <EditorStep
            videoUrl={videoUrl}
            videoId={videoId}
            topic={topic}
            onCreateAnother={handleCreateAnother}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {currentStep === STEPS.TOPIC && (
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 border-b border-primary-100">
          <div className="max-w-4xl mx-auto px-6 py-6 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">AI Faceless Video Creator</h1>
            <p className="text-gray-600 text-sm">
              Create original AI-generated videos — not stock footage. Prompt → Script → AI Visuals → Voiceover → Video.
            </p>
            <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500">
              <span>YouTube Shorts</span>
              <span>·</span>
              <span>TikTok</span>
              <span>·</span>
              <span>Instagram Reels</span>
            </div>
          </div>
        </div>
      )}
      {currentStep !== STEPS.TOPIC && (
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <StepNavigation currentStep={currentStep} completedSteps={completedSteps} />
          </div>
        </div>
      )}
      {error && currentStep !== STEPS.ASSETS && (
        <div className="max-w-4xl mx-auto px-6 pt-4">
          <div className="p-4 bg-error-50 border border-error-100 rounded-xl">
            <p className="text-error-600">{error}</p>
          </div>
        </div>
      )}
      {renderStep()}
    </>
  );
}
